import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import dayjs from 'dayjs';

// Import dayjs locales so relative time (fromNow, toNow, etc.) is translated
import 'dayjs/locale/de';
import 'dayjs/locale/bg';

// Import translation files
import enCommon from './locales/en/common.json';
import enAuth from './locales/en/auth.json';
import enNavigation from './locales/en/navigation.json';
import enDashboard from './locales/en/dashboard.json';
import enLanding from './locales/en/landing.json';
import enAdmin from './locales/en/admin.json';
import enManage from './locales/en/manage.json';
import enProfile from './locales/en/profile.json';

import deCommon from './locales/de/common.json';
import deAuth from './locales/de/auth.json';
import deNavigation from './locales/de/navigation.json';
import deDashboard from './locales/de/dashboard.json';
import deLanding from './locales/de/landing.json';
import deAdmin from './locales/de/admin.json';
import deManage from './locales/de/manage.json';
import deProfile from './locales/de/profile.json';

import bgCommon from './locales/bg/common.json';
import bgAuth from './locales/bg/auth.json';
import bgNavigation from './locales/bg/navigation.json';
import bgDashboard from './locales/bg/dashboard.json';
import bgLanding from './locales/bg/landing.json';
import bgAdmin from './locales/bg/admin.json';
import bgManage from './locales/bg/manage.json';
import bgProfile from './locales/bg/profile.json';

export const supportedLanguages = {
  en: { label: 'English', flag: '🇬🇧' },
  de: { label: 'Deutsch', flag: '🇩🇪' },
  bg: { label: 'Български', flag: '🇧🇬' }
} as const;

export type SupportedLanguage = keyof typeof supportedLanguages;

/** Array of supported language codes for iteration (e.g., modules providing translations) */
export const supportedLanguageCodes = Object.keys(supportedLanguages) as SupportedLanguage[];

export const defaultNS = 'common';

const STORAGE_KEY = 'i18nextLng';

/** Resolve initial language: saved preference → fallback to English (no browser detection). */
function getInitialLanguage(): SupportedLanguage {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && stored in supportedLanguages) return stored as SupportedLanguage;
  } catch { /* localStorage unavailable */ }
  return 'en';
}

export const resources = {
  en: {
    common: enCommon,
    auth: enAuth,
    navigation: enNavigation,
    dashboard: enDashboard,
    landing: enLanding,
    admin: enAdmin,
    manage: enManage,
    profile: enProfile
  },
  de: {
    common: deCommon,
    auth: deAuth,
    navigation: deNavigation,
    dashboard: deDashboard,
    landing: deLanding,
    admin: deAdmin,
    manage: deManage,
    profile: deProfile
  },
  bg: {
    common: bgCommon,
    auth: bgAuth,
    navigation: bgNavigation,
    dashboard: bgDashboard,
    landing: bgLanding,
    admin: bgAdmin,
    manage: bgManage,
    profile: bgProfile
  }
} as const;

i18n
  .use(initReactI18next)
  .init({
    resources,
    defaultNS,
    lng: getInitialLanguage(),
    fallbackLng: 'en',
    supportedLngs: Object.keys(supportedLanguages),

    interpolation: {
      escapeValue: false // React already escapes by default
    }
  });

// Sync dayjs locale with i18n language so that relative time strings
// (e.g. fromNow(), toNow()) are displayed in the correct language.
const syncDayjsLocale = (lng: string) => {
  dayjs.locale(lng);
};

// Set initial dayjs locale from the resolved language
syncDayjsLocale(i18n.language ?? 'en');

// Keep dayjs locale and localStorage in sync whenever the language changes
i18n.on('languageChanged', (lng: string) => {
  syncDayjsLocale(lng);
  try { localStorage.setItem(STORAGE_KEY, lng); } catch { /* ignore */ }
});

export default i18n;
